import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type Observe = (element: HTMLElement, onVisible: (visible: boolean) => void) => () => void;

/** Keep grid slots for native responsive layout, but release off-screen cards/media. */
export function VirtualizedPostGrid<T>({ items, itemKey, renderItem }: {
  items: T[];
  itemKey: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
}) {
  const grid = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const callbacks = useRef(new Map<HTMLElement, (visible: boolean) => void>());
  const observe = useCallback<Observe>((element, callback) => {
    callbacks.current.set(element, callback);
    observer.current?.observe(element);
    // Progressive fallback for environments without IntersectionObserver.
    if (typeof IntersectionObserver === "undefined") callback(true);
    return () => {
      observer.current?.unobserve(element);
      callbacks.current.delete(element);
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    let root = grid.current?.parentElement ?? null;
    while (root && root !== document.body && root !== document.documentElement &&
      !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) root = root.parentElement;
    if (root === document.body || root === document.documentElement) root = null;
    const instance = new IntersectionObserver(entries => {
      for (const entry of entries) callbacks.current.get(entry.target as HTMLElement)?.(entry.isIntersecting);
    }, { root, rootMargin: "800px 0px" });
    observer.current = instance;
    callbacks.current.forEach((_, element) => instance.observe(element));
    return () => {
      instance.disconnect();
      observer.current = null;
    };
  }, []);

  return (
    <div ref={grid} id="analytics-ranked-posts" role="list" aria-label="Ranked posts"
      className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
      {items.map((item, index) => (
        <VirtualPostSlot key={itemKey(item)} index={index} count={items.length} observe={observe}>
          {() => renderItem(item, index)}
        </VirtualPostSlot>
      ))}
    </div>
  );
}

function VirtualPostSlot({ index, count, observe, children }: {
  index: number;
  count: number;
  observe: Observe;
  children: () => ReactNode;
}) {
  const slot = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const transferFocus = useRef(false);
  const [visible, setVisible] = useState(index < 5);
  const [focused, setFocused] = useState(false);
  // The thumbnail scales at 16:10; remember the remaining measured card height.
  const [bodyHeight, setBodyHeight] = useState(284);
  const mounted = visible || focused;

  useEffect(() => observe(slot.current!, setVisible), [observe]);
  useLayoutEffect(() => {
    if (!mounted || !content.current) return;
    const element = content.current;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width && rect.height) {
        const next = Math.max(0, rect.height - rect.width * 10 / 16);
        setBodyHeight(previous => Math.abs(previous - next) > 0.5 ? next : previous);
      }
    };
    measure();
    const resize = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    resize?.observe(content.current);
    if (transferFocus.current) {
      transferFocus.current = false;
      content.current.querySelector<HTMLElement>("a[href], button:not([disabled])")?.focus({ preventScroll: true });
    }
    return () => resize?.disconnect();
  }, [mounted]);

  return (
    <div ref={slot} role="listitem" aria-posinset={index + 1} aria-setsize={count} className="min-w-0"
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}>
      {mounted ? (
        <div ref={content} className="h-full [&>article]:h-full">{children()}</div>
      ) : (
        <button type="button" className="block w-full rounded-xl bg-muted/30 text-left"
          aria-label={`Show post ${index + 1}`}
          onFocus={() => { transferFocus.current = true; setFocused(true); }}
          onClick={() => setVisible(true)}>
          <div className="aspect-[16/10]" aria-hidden />
          <div style={{ height: bodyHeight }} aria-hidden />
        </button>
      )}
    </div>
  );
}
